import { FC, ReactNode, createContext, useContext, useEffect, useState, useCallback } from 'react'
import { useWalletStore, useGameStore } from '@/store'
import type {
  GameSession,
  Difficulty,
  GeneratedSituation,
  GeneratedOutcome,
  GeneratedImage,
  DynamicAction,
  LifeEvent,
  Portfolio,
} from '@/types'
import type { MarketState } from '@/types'
import type { SimulatedMarketState, AIMarketEffect } from '@/types/simulatedMarket'
import { applyStoryEffects } from '@/game/market/simulatedMarketEngine'
import { useMarketUpdates } from '@/hooks/useMarketUpdates'
import {
  createNewGame,
  loadGame as loadGameEngine,
  executeBuy,
  executeSell,
  advanceTime,
  checkGameStatus,
  getAvailableTokensForSession,
  abandonGame,
} from '@/game/engine'
import type { MarketEvent, TradeResult } from '@/game'
import { gameMaster, getLoadingMessage } from '@/game/ai/gameMaster'

interface GameContextValue {
  // State
  isInitialized: boolean
  welcomeMessage: string | null
  timeSkipMessage: string | null
  lastTradeResult: (TradeResult & { updatedSession: GameSession }) | null
  marketEvents: MarketEvent[]

  // Game Master state
  currentSituation: GeneratedSituation | null
  currentOutcome: GeneratedOutcome | null
  currentSceneImage: GeneratedImage | null
  isGeneratingSituation: boolean
  isGeneratingOutcome: boolean
  loadingMessage: string

  // Dynamic actions state
  dynamicActions: DynamicAction[]
  isGeneratingActions: boolean

  // Game actions
  loadGame: () => Promise<void>
  startNewGame: (difficulty: Difficulty) => Promise<void>
  saveGame: () => Promise<void>

  // Trading actions
  buyToken: (token: string, usdcAmount: number) => Promise<TradeResult>
  sellToken: (token: string, amount: number) => Promise<TradeResult>

  // Time actions
  skipTime: (hours: number) => Promise<void>

  // Game Master actions
  generateSituation: () => Promise<void>
  makeChoice: (choiceId: string) => Promise<void>
  makeCustomChoice: (customAction: string) => Promise<void>
  skipSituation: () => void
  continuePlaying: () => void

  // Dynamic actions
  handleDynamicAction: (action: DynamicAction) => Promise<void>
  refreshDynamicActions: () => Promise<void>

  // Utility
  getAvailableTokens: () => ReturnType<typeof getAvailableTokensForSession>
  abandonCurrentGame: () => Promise<void>
  clearMessages: () => void
}

const GameContext = createContext<GameContextValue | null>(null)

export const useGame = () => {
  const context = useContext(GameContext)
  if (!context) {
    throw new Error('useGame must be used within GameProvider')
  }
  return context
}

// Helper function to apply life event financial impact to portfolio
// Deducts from USDC first, then sells assets if needed
// Adds to USDC for positive impacts
function applyLifeEventToPortfolio(portfolio: Portfolio, lifeEvent: LifeEvent, marketPrices: Record<string, { price: number }>): Portfolio {
  const impact = lifeEvent.financialImpact
  let newPortfolio = { ...portfolio, holdings: [...portfolio.holdings.map(h => ({ ...h }))] }

  if (impact >= 0) {
    // Positive impact - add to USDC
    const usdcHolding = newPortfolio.holdings.find(h => h.symbol === 'USDC')
    if (usdcHolding) {
      usdcHolding.amount += impact
    } else {
      newPortfolio.holdings.push({
        symbol: 'USDC',
        name: 'USD Coin',
        amount: impact,
        avgBuyPrice: 1,
        currentPrice: 1,
      })
    }
  } else {
    // Negative impact - deduct from USDC first, then sell assets
    let remaining = Math.abs(impact)

    // First, try to take from USDC
    const usdcHolding = newPortfolio.holdings.find(h => h.symbol === 'USDC')
    if (usdcHolding && usdcHolding.amount > 0) {
      const deductFromUsdc = Math.min(usdcHolding.amount, remaining)
      usdcHolding.amount -= deductFromUsdc
      remaining -= deductFromUsdc
    }

    // If still have remaining, sell other assets (largest holdings first)
    if (remaining > 0) {
      // Sort non-USDC holdings by value (descending)
      const sortedHoldings = newPortfolio.holdings
        .filter(h => h.symbol !== 'USDC' && h.amount > 0)
        .sort((a, b) => {
          const aValue = a.amount * (marketPrices[a.symbol]?.price || a.currentPrice)
          const bValue = b.amount * (marketPrices[b.symbol]?.price || b.currentPrice)
          return bValue - aValue
        })

      for (const holding of sortedHoldings) {
        if (remaining <= 0) break

        const price = marketPrices[holding.symbol]?.price || holding.currentPrice
        const holdingValue = holding.amount * price

        if (holdingValue <= remaining) {
          // Sell entire holding
          remaining -= holdingValue
          holding.amount = 0
        } else {
          // Sell partial holding
          const amountToSell = remaining / price
          holding.amount -= amountToSell
          remaining = 0
        }
      }
    }

    // Remove empty holdings (except USDC)
    newPortfolio.holdings = newPortfolio.holdings.filter(h => h.symbol === 'USDC' || h.amount > 0)
  }

  // Recalculate total value and cash balance
  newPortfolio.cashBalance = newPortfolio.holdings.find(h => h.symbol === 'USDC')?.amount || 0
  newPortfolio.totalValue = newPortfolio.holdings.reduce((sum, h) => {
    const price = marketPrices[h.symbol]?.price || h.currentPrice
    return sum + (h.amount * price)
  }, 0)

  return newPortfolio
}

// NOTE: No hard cap on decisions - games end organically via:
// - Hitting goal target (win)
// - Going bankrupt - portfolio <= 0 (lose)
// - Death or imprisonment life events (lose)
// The AI gradually escalates risk to push toward natural endings

// Helper function to apply outcome effects to session
// NOTE: cashChange is intentionally NOT applied - portfolio only changes through trades or life events
function applyOutcomeEffects(session: GameSession, outcome: GeneratedOutcome): GameSession {
  const { effects } = outcome
  let updatedSession = { ...session }

  // Increment decision count
  updatedSession = {
    ...updatedSession,
    decisionCount: (updatedSession.decisionCount || 0) + 1,
  }

  // Apply reputation change
  if (effects.reputationChange) {
    const newRep = Math.max(0, Math.min(100, updatedSession.player.reputation + effects.reputationChange))
    updatedSession = {
      ...updatedSession,
      player: {
        ...updatedSession.player,
        reputation: newRep,
      },
    }
  }

  // Apply trait changes
  if (effects.traitChanges) {
    const newTraits = { ...updatedSession.player.traits }
    for (const [trait, change] of Object.entries(effects.traitChanges)) {
      if (change !== undefined && trait in newTraits) {
        const key = trait as keyof typeof newTraits
        newTraits[key] = Math.max(0, Math.min(100, newTraits[key] + change))
      }
    }
    updatedSession = {
      ...updatedSession,
      player: {
        ...updatedSession.player,
        traits: newTraits,
      },
    }
  }

  // Apply market effects (if any)
  if (outcome.marketEffects && outcome.marketEffects.length > 0) {
    const newPrices = { ...updatedSession.market.prices }
    for (const effect of outcome.marketEffects) {
      const existingPrice = newPrices[effect.token]
      if (existingPrice) {
        const currentPrice = existingPrice.price
        const newPrice = currentPrice * (1 + effect.priceImpact / 100)
        newPrices[effect.token] = {
          ...existingPrice,
          price: newPrice,
          change24h: existingPrice.change24h + effect.priceImpact,
        }
      }
    }
    updatedSession = {
      ...updatedSession,
      market: {
        ...updatedSession.market,
        prices: newPrices,
      },
    }
  }

  // Apply life event (if any)
  if (effects.lifeEvent) {
    const lifeEvent = effects.lifeEvent

    // Apply financial impact to portfolio
    if (lifeEvent.financialImpact !== 0) {
      const newPortfolio = applyLifeEventToPortfolio(
        updatedSession.player.portfolio,
        lifeEvent,
        updatedSession.market.prices
      )
      updatedSession = {
        ...updatedSession,
        player: {
          ...updatedSession.player,
          portfolio: newPortfolio,
        },
      }
    }

    // Handle game-ending events
    if (lifeEvent.isGameEnding) {
      if (lifeEvent.type === 'death') {
        updatedSession = {
          ...updatedSession,
          status: 'lost',
        }
      } else if (lifeEvent.type === 'imprisoned') {
        updatedSession = {
          ...updatedSession,
          status: 'lost',
        }
      }
    }
  }

  // Check for bankruptcy - instant game over
  if (updatedSession.player.portfolio.totalValue <= 0 && updatedSession.status === 'active') {
    updatedSession = {
      ...updatedSession,
      status: 'lost',
    }
  }

  // Check for win condition at any point
  if (updatedSession.status === 'active') {
    const portfolioValue = updatedSession.player.portfolio.totalValue
    const targetAmount = updatedSession.goal.targetAmount
    if (portfolioValue >= targetAmount) {
      updatedSession = {
        ...updatedSession,
        status: 'won',
      }
    }
  }

  return updatedSession
}

interface Props {
  children: ReactNode
}

export const GameProvider: FC<Props> = ({ children }) => {
  const { publicKey, connected, isAuthenticated } = useWalletStore()
  const {
    session,
    setSession,
    setLoading,
    setError,
    currentSituation,
    currentOutcome,
    currentSceneImage,
    isGeneratingSituation,
    isGeneratingOutcome,
    setCurrentSituation,
    setCurrentOutcome,
    setCurrentSceneImage,
    setGeneratingSituation,
    setGeneratingOutcome,
    addPlayerAction,
    addConsequence,
    addGeneratedSituationId,
    clearSituationState,
  } = useGameStore()

  const [welcomeMessage, setWelcomeMessage] = useState<string | null>(null)
  const [timeSkipMessage, setTimeSkipMessage] = useState<string | null>(null)
  const [lastTradeResult, setLastTradeResult] = useState<(TradeResult & { updatedSession: GameSession }) | null>(null)
  const [marketEvents, setMarketEvents] = useState<MarketEvent[]>([])
  const [loadingMessage, setLoadingMessage] = useState<string>(getLoadingMessage())

  // Dynamic actions state
  const [dynamicActions, setDynamicActions] = useState<DynamicAction[]>([])
  const [isGeneratingActions, setIsGeneratingActions] = useState(false)

  // Simulated market state (for real-time price updates)
  const [simulatedMarket, setSimulatedMarket] = useState<SimulatedMarketState | null>(null)

  // Use market updates hook for real-time price simulation
  useMarketUpdates(session, simulatedMarket, {
    enabled: session?.status === 'active',
    onMarketEvent: (event) => {
      // Add to market events for display
      setMarketEvents(prev => [event as unknown as MarketEvent, ...prev].slice(0, 10))
    },
  })

  // Load existing game when wallet connects and authenticates
  useEffect(() => {
    if (connected && publicKey && isAuthenticated) {
      loadGame()
    }
  }, [connected, publicKey, isAuthenticated])

  const loadGame = async () => {
    if (!publicKey) return

    setLoading(true)
    setError(null)

    try {
      const result = await loadGameEngine(publicKey)

      if (result) {
        setSession(result.session)
        setMarketEvents(result.marketEvents)
        setSimulatedMarket(result.simulatedMarket)

        if (result.timeSkipMessage) {
          setTimeSkipMessage(result.timeSkipMessage)
        }

        // Check if game ended while away
        const statusCheck = await checkGameStatus(result.session)
        if (statusCheck.status !== 'active') {
          setTimeSkipMessage(statusCheck.message)
        }
      } else {
        setSession(null)
        setSimulatedMarket(null)
      }
    } catch (error) {
      console.error('Failed to load game:', error)
      setError('Failed to load game')
    } finally {
      setLoading(false)
    }
  }

  const startNewGame = async (difficulty: Difficulty) => {
    if (!publicKey) {
      setError('Wallet not connected')
      return
    }

    if (!isAuthenticated) {
      setError('Please sign in first')
      return
    }

    setLoading(true)
    setError(null)
    setWelcomeMessage(null)
    setTimeSkipMessage(null)
    setMarketEvents([])

    try {
      const { session: newSession, welcomeMessage: msg, simulatedMarket: simMarket } = await createNewGame(
        publicKey,
        difficulty
      )

      setSession(newSession)
      setWelcomeMessage(msg)
      setSimulatedMarket(simMarket)
    } catch (error) {
      console.error('Failed to start game:', error)
      const msg = error instanceof Error ? error.message : String(error)
      setError(`Failed to start new game: ${msg}`)
    } finally {
      setLoading(false)
    }
  }

  const saveGame = async () => {
    // Game is auto-saved after each action
    // This is here for manual save if needed
    console.log('Game state is auto-saved')
  }

  const buyToken = async (token: string, usdcAmount: number): Promise<TradeResult> => {
    if (!session) {
      return {
        success: false,
        newPortfolio: { holdings: [], cashBalance: 0, totalValue: 0 },
        newMarket: {} as MarketState,
        message: 'No active game session',
        degenQuote: 'Start a game first, anon.',
      }
    }

    setLoading(true)

    try {
      const result = await executeBuy(session, token, usdcAmount)
      setLastTradeResult(result)

      if (result.success) {
        setSession(result.updatedSession)

        // Check win condition
        const statusCheck = await checkGameStatus(result.updatedSession)
        if (statusCheck.status !== 'active') {
          setTimeSkipMessage(statusCheck.message)
        }
      }

      return result
    } catch (error) {
      console.error('Trade failed:', error)
      return {
        success: false,
        newPortfolio: session.player.portfolio,
        newMarket: session.market,
        message: 'Trade execution failed',
        degenQuote: 'Something went wrong. The blockchain gods are not pleased.',
      }
    } finally {
      setLoading(false)
    }
  }

  const sellToken = async (token: string, amount: number): Promise<TradeResult> => {
    if (!session) {
      return {
        success: false,
        newPortfolio: { holdings: [], cashBalance: 0, totalValue: 0 },
        newMarket: {} as MarketState,
        message: 'No active game session',
        degenQuote: 'Start a game first, anon.',
      }
    }

    setLoading(true)

    try {
      const result = await executeSell(session, token, amount)
      setLastTradeResult(result)

      if (result.success) {
        setSession(result.updatedSession)

        // Check win condition
        const statusCheck = await checkGameStatus(result.updatedSession)
        if (statusCheck.status !== 'active') {
          setTimeSkipMessage(statusCheck.message)
        }
      }

      return result
    } catch (error) {
      console.error('Trade failed:', error)
      return {
        success: false,
        newPortfolio: session.player.portfolio,
        newMarket: session.market,
        message: 'Trade execution failed',
        degenQuote: 'Something went wrong. Maybe try turning it off and on again?',
      }
    } finally {
      setLoading(false)
    }
  }

  const skipTime = async (hours: number) => {
    if (!session) return

    setLoading(true)

    try {
      const { updatedSession, marketEvents: events } = await advanceTime(session, hours)
      setSession(updatedSession)
      setMarketEvents(events)

      if (events.length > 0) {
        const eventSummary = events
          .map(e => `- ${e.headline}`)
          .join('\n')
        setTimeSkipMessage(`${hours} hours passed...\n\n${eventSummary}`)
      }

      // Check game status
      const statusCheck = await checkGameStatus(updatedSession)
      if (statusCheck.status !== 'active') {
        setTimeSkipMessage(statusCheck.message)
      }
    } catch (error) {
      console.error('Time skip failed:', error)
    } finally {
      setLoading(false)
    }
  }

  const getAvailableTokens = () => {
    if (!session) return []
    return getAvailableTokensForSession(session)
  }

  const abandonCurrentGame = async () => {
    if (!session) return

    try {
      await abandonGame(session)
      setSession(null)
      setWelcomeMessage(null)
      setTimeSkipMessage(null)
      setMarketEvents([])
    } catch (error) {
      console.error('Failed to abandon game:', error)
    }
  }

  const clearMessages = () => {
    setWelcomeMessage(null)
    setTimeSkipMessage(null)
    setLastTradeResult(null)
  }

  // ==========================================
  // Game Master Functions
  // ==========================================

  // Generate a new situation
  const generateSituation = useCallback(async () => {
    if (!session || isGeneratingSituation) return

    setGeneratingSituation(true)
    setLoadingMessage(getLoadingMessage())

    // Update loading message periodically
    const messageInterval = setInterval(() => {
      setLoadingMessage(getLoadingMessage())
    }, 3000)

    try {
      const situation = await gameMaster.generateSituation(session, { trigger: 'manual' })
      setCurrentSituation(situation)
      addGeneratedSituationId(situation.id)

      // Generate scene image in background
      gameMaster.generateSceneImage(situation).then((image) => {
        setCurrentSceneImage(image)
      }).catch(console.error)
    } catch (error) {
      console.error('Failed to generate situation:', error)
      // Extract the actual error message for display
      const errorMessage = error instanceof Error ? error.message : String(error)
      // Firebase errors have a 'message' property we can use
      const displayMessage = errorMessage.includes(':')
        ? (errorMessage.split(':').pop() ?? errorMessage).trim()
        : errorMessage
      setError(`Failed to generate situation: ${displayMessage}`)
    } finally {
      clearInterval(messageInterval)
      setGeneratingSituation(false)
    }
  }, [session, isGeneratingSituation, setGeneratingSituation, setCurrentSituation, addGeneratedSituationId, setCurrentSceneImage, setError])

  // Make a choice in the current situation
  const makeChoice = useCallback(async (choiceId: string) => {
    if (!session || !currentSituation || isGeneratingOutcome) return

    setGeneratingOutcome(true)
    setLoadingMessage(getLoadingMessage())

    try {
      const { outcome, action, consequence } = await gameMaster.resolveChoice(session, choiceId)

      // Update state
      setCurrentOutcome(outcome)
      addPlayerAction(action)

      if (consequence) {
        addConsequence(consequence)
      }

      // Apply effects to session
      if (outcome.effects) {
        const updatedSession = applyOutcomeEffects(session, outcome)
        setSession(updatedSession)

        // Apply market effects to simulated market for real-time updates
        if (outcome.marketEffects && outcome.marketEffects.length > 0 && simulatedMarket) {
          const marketEffects: AIMarketEffect[] = outcome.marketEffects.map(e => ({
            token: e.token,
            priceImpact: e.priceImpact,
            reason: e.reason || 'Story event',
          }))
          const updatedSimMarket = applyStoryEffects(simulatedMarket, marketEffects)
          setSimulatedMarket(updatedSimMarket)
        }

        // Check win/lose conditions
        const statusCheck = await checkGameStatus(updatedSession)
        if (statusCheck.status !== 'active') {
          setTimeSkipMessage(statusCheck.message)
        }
      }
    } catch (error) {
      console.error('Failed to resolve choice:', error)
      setError('Failed to resolve choice. Try again.')
    } finally {
      setGeneratingOutcome(false)
    }
  }, [session, currentSituation, isGeneratingOutcome, setGeneratingOutcome, setCurrentOutcome, addPlayerAction, addConsequence, setSession, setError, simulatedMarket])

  // Make a custom choice (user-defined action) - adventure game style
  // Works with or without an active situation
  const makeCustomChoice = useCallback(async (customAction: string) => {
    // Always read the latest session from the store to avoid stale closure data
    // (e.g. after a trade updates the portfolio, the closure's `session` is stale)
    const currentSession = useGameStore.getState().session
    if (!currentSession || isGeneratingSituation || isGeneratingOutcome) return

    // If there's a current outcome (user already saw result), clear it and start fresh
    // This allows continuous play without an intermediate "WHAT DO YOU DO?" screen
    if (currentOutcome) {
      // Set loading state BEFORE clearing so there's no flash of empty state
      setGeneratingSituation(true)
      setLoadingMessage(getLoadingMessage())

      // Now clear the old state
      clearSituationState()

      // Update loading message periodically
      const messageInterval = setInterval(() => {
        setLoadingMessage(getLoadingMessage())
      }, 3000)

      try {
        // Add the player action to history
        addPlayerAction({
          id: `action-${Date.now()}`,
          type: 'choice',
          description: customAction,
          timestamp: new Date(),
        })

        // Generate a fresh situation based on the player's action
        const situation = await gameMaster.generateSituationFromAction(currentSession, customAction)
        setCurrentSituation(situation)
        addGeneratedSituationId(situation.id)

        // Generate scene image in background
        gameMaster.generateSceneImage(situation).then((image) => {
          setCurrentSceneImage(image)
        }).catch(console.error)
      } catch (error) {
        console.error('Failed to generate situation from action:', error)
        const errorMessage = error instanceof Error ? error.message : String(error)
        const displayMessage = errorMessage.includes(':')
          ? (errorMessage.split(':').pop() ?? errorMessage).trim()
          : errorMessage
        setError(`Failed to process action: ${displayMessage}`)
      } finally {
        clearInterval(messageInterval)
        setGeneratingSituation(false)
      }
      return
    }

    // If there's a current situation (but no outcome yet), resolve it with the custom action
    if (currentSituation) {
      setGeneratingOutcome(true)
      setLoadingMessage(getLoadingMessage())

      try {
        const { outcome, action, consequence } = await gameMaster.resolveCustomChoice(currentSession, customAction, currentSituation)

        // Update state
        setCurrentOutcome(outcome)
        addPlayerAction(action)

        if (consequence) {
          addConsequence(consequence)
        }

        // Apply effects to session - re-read latest in case trade just updated it
        if (outcome.effects) {
          const latestSession = useGameStore.getState().session || currentSession
          const updatedSession = applyOutcomeEffects(latestSession, outcome)
          setSession(updatedSession)

          // Apply market effects to simulated market for real-time updates
          if (outcome.marketEffects && outcome.marketEffects.length > 0 && simulatedMarket) {
            const marketEffects: AIMarketEffect[] = outcome.marketEffects.map(e => ({
              token: e.token,
              priceImpact: e.priceImpact,
              reason: e.reason || 'Story event',
            }))
            const updatedSimMarket = applyStoryEffects(simulatedMarket, marketEffects)
            setSimulatedMarket(updatedSimMarket)
          }

          // Check win/lose conditions
          const statusCheck = await checkGameStatus(updatedSession)
          if (statusCheck.status !== 'active') {
            setTimeSkipMessage(statusCheck.message)
          }
        }
      } catch (error) {
        console.error('Failed to resolve custom choice:', error)
        setError('Failed to resolve your action. Try again.')
      } finally {
        setGeneratingOutcome(false)
      }
    } else {
      // No current situation - generate one based on the player's free-form action
      setGeneratingSituation(true)
      setLoadingMessage(getLoadingMessage())

      // Update loading message periodically
      const messageInterval = setInterval(() => {
        setLoadingMessage(getLoadingMessage())
      }, 3000)

      try {
        // Add the player action to history first so AI can reference it
        addPlayerAction({
          id: `action-${Date.now()}`,
          type: 'choice',
          description: customAction,
          timestamp: new Date(),
        })

        // Generate a situation as a response to the player's action
        const situation = await gameMaster.generateSituationFromAction(currentSession, customAction)
        setCurrentSituation(situation)
        addGeneratedSituationId(situation.id)

        // Generate scene image in background
        gameMaster.generateSceneImage(situation).then((image) => {
          setCurrentSceneImage(image)
        }).catch(console.error)
      } catch (error) {
        console.error('Failed to generate situation from action:', error)
        const errorMessage = error instanceof Error ? error.message : String(error)
        const displayMessage = errorMessage.includes(':')
          ? (errorMessage.split(':').pop() ?? errorMessage).trim()
          : errorMessage
        setError(`Failed to process action: ${displayMessage}`)
      } finally {
        clearInterval(messageInterval)
        setGeneratingSituation(false)
      }
    }
  }, [currentSituation, isGeneratingSituation, isGeneratingOutcome, setGeneratingSituation, setGeneratingOutcome, setCurrentOutcome, setCurrentSituation, addPlayerAction, addConsequence, addGeneratedSituationId, setSession, setCurrentSceneImage, setError])

  // Skip the current situation
  const skipSituation = useCallback(() => {
    if (currentSituation) {
      addPlayerAction({
        id: `skip-${Date.now()}`,
        type: 'choice',
        description: `Skipped: ${currentSituation.title}`,
        timestamp: new Date(),
        situationId: currentSituation.id,
      })
    }
    clearSituationState()
  }, [currentSituation, addPlayerAction, clearSituationState])

  // Continue after outcome (clear and optionally generate new)
  const continuePlaying = useCallback(() => {
    clearSituationState()
  }, [clearSituationState])

  // ==========================================
  // Dynamic Actions
  // ==========================================

  // Refresh dynamic actions based on current state
  const refreshDynamicActions = useCallback(async () => {
    if (!session || isGeneratingActions) return

    setIsGeneratingActions(true)
    try {
      const actions = await gameMaster.generateDynamicActions(session)
      setDynamicActions(actions)
    } catch (error) {
      console.error('Failed to generate dynamic actions:', error)
    } finally {
      setIsGeneratingActions(false)
    }
  }, [session, isGeneratingActions])

  // Handle a dynamic action selection
  const handleDynamicAction = useCallback(async (action: DynamicAction) => {
    if (!session) return

    // Skip time if action has gameHoursSkip
    if (action.gameHoursSkip > 0) {
      await skipTime(action.gameHoursSkip)
    }

    // If action has trigger context, generate a situation with that context
    if (action.triggerContext) {
      setGeneratingSituation(true)
      setLoadingMessage(getLoadingMessage())

      try {
        // Generate situation influenced by the action context
        const situation = await gameMaster.generateSituation(session, {
          trigger: 'player_action',
          // The action's triggerContext will be considered via recent actions
        })
        setCurrentSituation(situation)
        addGeneratedSituationId(situation.id)

        // Add action to player history
        addPlayerAction({
          id: `action-${Date.now()}`,
          type: 'choice',
          description: `${action.label}: ${action.description}`,
          timestamp: new Date(),
        })

        // Generate scene image in background
        gameMaster.generateSceneImage(situation).then((image) => {
          setCurrentSceneImage(image)
        }).catch(console.error)
      } catch (error) {
        console.error('Failed to generate situation from dynamic action:', error)
      } finally {
        setGeneratingSituation(false)
      }
    }

    // Refresh dynamic actions after taking one
    refreshDynamicActions()
  }, [session, skipTime, setGeneratingSituation, setCurrentSituation, addGeneratedSituationId, addPlayerAction, setCurrentSceneImage, refreshDynamicActions])

  // Generate initial dynamic actions when session loads and no situation active
  useEffect(() => {
    if (session && !currentSituation && !currentOutcome && dynamicActions.length === 0 && !isGeneratingActions) {
      refreshDynamicActions()
    }
  }, [session?.id, currentSituation, currentOutcome])

  // Refresh actions when situation clears
  useEffect(() => {
    if (session && !currentSituation && !currentOutcome && !isGeneratingActions) {
      refreshDynamicActions()
    }
  }, [currentSituation, currentOutcome])

  // Reset Game Master when game resets
  useEffect(() => {
    if (!session) {
      gameMaster.reset()
    }
  }, [session])

  const contextValue: GameContextValue = {
    isInitialized: !!publicKey && isAuthenticated,
    welcomeMessage,
    timeSkipMessage,
    lastTradeResult,
    marketEvents,

    // Game Master state
    currentSituation,
    currentOutcome,
    currentSceneImage,
    isGeneratingSituation,
    isGeneratingOutcome,
    loadingMessage,

    // Dynamic actions state
    dynamicActions,
    isGeneratingActions,

    // Game actions
    loadGame,
    startNewGame,
    saveGame,
    buyToken,
    sellToken,
    skipTime,

    // Game Master actions
    generateSituation,
    makeChoice,
    makeCustomChoice,
    skipSituation,
    continuePlaying,

    // Dynamic actions
    handleDynamicAction,
    refreshDynamicActions,

    // Utility
    getAvailableTokens,
    abandonCurrentGame,
    clearMessages,
  }

  return (
    <GameContext.Provider value={contextValue}>{children}</GameContext.Provider>
  )
}
